import { CustomTooltip } from '@/shared/ui/components';
import { DOUBLE_HYPHEN_BULLET } from '@/shared/config';
import { convertDeviceModel } from '@/shared/lib';
import _ from 'lodash';
import { t } from 'ttag';
import { ReasonRMA } from '../reasonRma';
import PatientImpactDrawer from './patientImpactDrawer/patientImpactDrawer';
import { useDeviceInformationTable } from './useDeviceInformationTable';
import QuestionMarkIcon from './assets/question-mark-circle.svg';
import type { DeviceInformationTableProps } from './deviceInformationTable.types';
import './styles.scss';

const DeviceInformationTable = ({
  isInbound = false,
  hasRenewalStock = false,
  rmaDevicesArray,
}: DeviceInformationTableProps) => {
  const { state, toggleDisplayDrawer } = useDeviceInformationTable();

  /**
   * Renders one device row of the RMA device information table in either the inbound or the outbound layout. The inbound row shows the number, device id with label index, converted model, a purpose column spelling out replace, add new, transfer or repair with renewal wording and the returning status, while the outbound row shows the replacement device when renewal stock exists plus the RMA reasons, patient impact, date of event and shipping status.
   *
   * @param item - Device issue row to render.
   * @param index - Zero-based row index, used as the key and displayed number.
   * @returns The table row element for the device.
   */
  const renderDeviceIssues = (item: any, index: any) =>
    isInbound ? (
      <tr className="item-row" key={index}>
        <td>
          <div className="number-id">{index + 1}</div>
        </td>

        <td>
          <div className="device-id">
            <div>{item.deviceId}</div>
            {item.labelIndex && <div className="label-index">{`Label #${item.labelIndex}`}</div>}
          </div>
        </td>

        <td>
          <div className="model">{convertDeviceModel(item.model) || DOUBLE_HYPHEN_BULLET}</div>
        </td>

        <td>
          <div className="purpose">
            {item?.purpose ? (
              item.purpose === 'Replace' ? (
                <div className="flex">
                  <span>Replacing &nbsp;</span>
                  <span>{item.replacedDevice?.deviceId}</span>
                </div>
              ) : item?.isRenewal ? (
                item.purpose === 'New' ? (
                  'Add new - Renewal Replenish'
                ) : item.purpose === 'Transfer' ? (
                  'Device transfer - Renewal'
                ) : (
                  'Repair - Renewal Replenish'
                )
              ) : item.purpose === 'New' ? (
                'Add new'
              ) : item.purpose === 'Transfer' ? (
                'Device transfer'
              ) : (
                'Repair'
              )
            ) : (
              DOUBLE_HYPHEN_BULLET
            )}
          </div>
        </td>

        <td>
          <div className="returning-status">{item.shippingStatus || DOUBLE_HYPHEN_BULLET}</div>
        </td>
      </tr>
    ) : (
      <tr className="item-row" key={index}>
        <td>
          <div className="number-id">{index + 1}</div>
        </td>

        <td>
          <div className="device-id">
            <div>{item.deviceId}</div>
            {item.labelIndex && <div className="label-index">{`Label #${item.labelIndex}`}</div>}
          </div>
        </td>

        <td>
          <div className="model">{convertDeviceModel(item.model) || DOUBLE_HYPHEN_BULLET}</div>
        </td>
        {hasRenewalStock && (
          <td>
            <div className="replacement-device-id">
              {item.replacedDevice ? item.replacedDevice?.deviceId : 'No replacement'}
            </div>
          </td>
        )}

        <td>
          <ReasonRMA
            classNameItem="other-reasons"
            classNameItemTitle="other"
            classNameItemOther="reasons"
            className="rma-reasons"
            data={item}
          />
        </td>

        <td>
          <div className="patient-impact">{item.patientImpact?.label}</div>
        </td>

        <td>
          <div className="date-of-event">{item.dateOfEvent}</div>
        </td>

        <td>
          <div className="shipping-status">{item.shippingStatus || DOUBLE_HYPHEN_BULLET}</div>
        </td>
      </tr>
    );

  return (
    <>
      <PatientImpactDrawer
        open={state.isVisiblePatientImpactDrawer}
        title={t`Patient impact`}
        onCancel={toggleDisplayDrawer}
      />

      <div className="device-issue-table">
        <div className="title">{t`RMA device information`}</div>
        <div className="table-device-wrapper">
          <table className="table-device-issues" aria-label={t`RMA device information`}>
            {isInbound ? (
              <tr className="title-row --inbound">
                <th className="row-number">
                  <div className="number-id">#</div>
                </th>
                <th className="row-device">
                  <div className="device-id">{t`Device ID`}</div>
                </th>
                <th className="row-model">
                  <div className="model">{t`Model`}</div>
                </th>
                <th className="row-purpose">
                  <div className="purpose">{t`Purpose`}</div>
                </th>
                <th className="row-shipping">
                  <div className="shipping-status">{t`Returning status`}</div>
                </th>
              </tr>
            ) : (
              <tr className="title-row --outbound">
                <th className="row-number">
                  <div className="number-id">#</div>
                </th>
                <th className="row-device">
                  <div className="device-id">{t`Device ID`}</div>
                </th>
                <th className="row-model">
                  <div className="model">{t`Model`}</div>
                </th>
                {hasRenewalStock && (
                  <th className="row-replacement">
                    <div className="replacement-device-id">{t`Replacement device ID`}</div>
                  </th>
                )}
                <th className="row-reason">
                  <div className="device-issues">{t`Reason for RMA`}</div>
                </th>
                <th className="row-patient">
                  <div className="patient-impact">
                    {t`Patient impact`}
                    <CustomTooltip title={t`Learn more about patient impact`} placement="bottom">
                      <span
                        className="question-icon"
                        onClick={toggleDisplayDrawer}
                        aria-hidden="true"
                      >
                        <img src={QuestionMarkIcon} alt="Question mark icon" />
                      </span>
                    </CustomTooltip>
                  </div>
                </th>
                <th className="row-date">
                  <div className="date-of-event">{t`Date of event`}</div>
                </th>
                <th className="row-shipping">
                  <div className="shipping-status">{t`Shipping status`}</div>
                </th>
              </tr>
            )}

            {rmaDevicesArray?.length > 0 ? (
              _.map(rmaDevicesArray, (item, index) => renderDeviceIssues(item, index))
            ) : (
              <tr className="item-row">
                <td colSpan={'100%' as any} className="no-data">
                  {t`There is no data to display`}
                </td>
              </tr>
            )}
          </table>
        </div>
      </div>
    </>
  );
};

export default DeviceInformationTable;
